import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback } from 'react';

// MP4 is the preferred output so iOS Safari can play the result. Chrome on Mac
// supports MP4 recording in recent versions; we fall back to webm if not.
const CANDIDATE_MIME_TYPES = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm',
];

function pickSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of CANDIDATE_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

export interface RecordingMeta {
  queueItemId: number;
  historyId: number;
  songTitle: string;
  artist: string;
  userName: string;
}

export interface CameraRecorderHandle {
  start: (meta: RecordingMeta) => boolean;
  stop: () => Promise<{ blob: Blob; meta: RecordingMeta; mimeType: string } | null>;
  cancel: () => void;
  isRecording: () => boolean;
}

interface Props {
  enabled: boolean;
  onCameraStateChange?: (cameraOn: boolean) => void;
  onRecordingStateChange?: (recording: boolean) => void;
}

const CameraRecorder = forwardRef<CameraRecorderHandle, Props>(({ enabled, onCameraStateChange, onRecordingStateChange }, ref) => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastTick, setLastTick] = useState<Date | null>(null);
  const [tabHidden, setTabHidden] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const metaRef = useRef<RecordingMeta | null>(null);
  const mimeTypeRef = useRef<string>('');

  // Enumerate cameras and mics whenever we have permission
  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');
      const mics = all.filter(d => d.kind === 'audioinput');
      setDevices(cams);
      setAudioDevices(mics);
      if (!selectedDeviceId && cams.length > 0) setSelectedDeviceId(cams[0].deviceId);
      if (!selectedAudioDeviceId && mics.length > 0) setSelectedAudioDeviceId(mics[0].deviceId);
    } catch (err) {
      console.warn('enumerateDevices failed', err);
    }
  }, [selectedDeviceId, selectedAudioDeviceId]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const constraints: MediaStreamConstraints = {
        video: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: selectedAudioDeviceId
          ? { deviceId: { exact: selectedAudioDeviceId } }
          : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOn(true);
      refreshDevices();
    } catch (err: any) {
      console.error('getUserMedia failed', err);
      setError(err?.message || 'Could not access camera');
      setCameraOn(false);
    }
  }, [selectedDeviceId, refreshDevices]);

  const stopCamera = useCallback(() => {
    stopStream();
    setCameraOn(false);
    setRecording(false);
    recorderRef.current = null;
    chunksRef.current = [];
    metaRef.current = null;
  }, [stopStream]);

  // Tear down on unmount
  useEffect(() => stopCamera, [stopCamera]);

  // Notify parent whenever camera state flips
  useEffect(() => {
    onCameraStateChange?.(cameraOn);
  }, [cameraOn, onCameraStateChange]);

  // Notify parent whenever recording state flips
  useEffect(() => {
    onRecordingStateChange?.(recording);
  }, [recording, onRecordingStateChange]);

  // Restart stream when device changes while camera is on
  useEffect(() => {
    if (cameraOn && (selectedDeviceId || selectedAudioDeviceId)) {
      stopStream();
      startCamera();
    }
  }, [selectedDeviceId, selectedAudioDeviceId, cameraOn, startCamera, stopStream]);

  // Page visibility warning — flash a banner if the tab gets backgrounded mid-record
  useEffect(() => {
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Heartbeat for the "recorder alive" indicator
  useEffect(() => {
    if (!cameraOn) return;
    const id = setInterval(() => setLastTick(new Date()), 2000);
    setLastTick(new Date());
    return () => clearInterval(id);
  }, [cameraOn]);

  useImperativeHandle(ref, () => ({
    start: (meta) => {
      if (!streamRef.current) {
        setError('Camera not ready');
        return false;
      }
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        console.warn('Recorder already active; ignoring start');
        return false;
      }
      const mimeType = pickSupportedMimeType();
      if (!mimeType) {
        setError('No supported recording format in this browser');
        return false;
      }
      try {
        const recorder = new MediaRecorder(streamRef.current, { mimeType });
        chunksRef.current = [];
        metaRef.current = meta;
        mimeTypeRef.current = mimeType;
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.start(1000); // request chunks every 1s so a crash loses at most ~1s
        recorderRef.current = recorder;
        setRecording(true);
        return true;
      } catch (err: any) {
        console.error('MediaRecorder.start failed', err);
        setError(err?.message || 'Failed to start recording');
        return false;
      }
    },
    stop: () => new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setRecording(false);
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        const meta = metaRef.current;
        chunksRef.current = [];
        metaRef.current = null;
        recorderRef.current = null;
        setRecording(false);
        if (!meta) { resolve(null); return; }
        resolve({ blob, meta, mimeType: mimeTypeRef.current });
      };
      recorder.stop();
    }),
    cancel: () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        try { recorder.stop(); } catch { /* ignore */ }
      }
      chunksRef.current = [];
      metaRef.current = null;
      recorderRef.current = null;
      setRecording(false);
    },
    isRecording: () => recording,
  }));

  if (!enabled) return null;

  return (
    <div style={{
      border: '1px solid #5a3a1a',
      borderRadius: 8,
      padding: 10,
      backgroundColor: 'rgba(40,25,15,0.95)',
      marginBottom: 16,
      display: 'flex',
      gap: 14,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: 200,
          height: 120,
          backgroundColor: '#0d0807',
          border: '1px solid #5a3a1a',
          borderRadius: 6,
          objectFit: 'cover',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f5deb3', fontSize: 14 }}>
          <span style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: recording ? '#e74c3c' : cameraOn ? '#2ecc71' : '#5a3a1a',
            boxShadow: recording ? '0 0 8px #e74c3c' : 'none',
          }} />
          <strong>
            {recording ? 'Recording' : cameraOn ? 'Camera ready' : 'Camera off'}
          </strong>
          {lastTick && cameraOn && (
            <span style={{ color: '#806040', fontSize: 11 }}>
              ({lastTick.toLocaleTimeString()})
            </span>
          )}
        </div>

        {devices.length > 0 && (
          <select
            value={selectedDeviceId || ''}
            onChange={e => setSelectedDeviceId(e.target.value)}
            disabled={recording}
            style={{
              padding: '4px 6px',
              backgroundColor: '#28190f',
              border: '1px solid #5a3a1a',
              borderRadius: 4,
              color: '#f5deb3',
              fontSize: 12,
            }}
          >
            {devices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
        )}

        {audioDevices.length > 0 && (
          <select
            value={selectedAudioDeviceId || ''}
            onChange={e => setSelectedAudioDeviceId(e.target.value)}
            disabled={recording}
            style={{
              padding: '4px 6px',
              backgroundColor: '#28190f',
              border: '1px solid #5a3a1a',
              borderRadius: 4,
              color: '#f5deb3',
              fontSize: 12,
            }}
          >
            {audioDevices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Mic ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {!cameraOn ? (
            <button
              onClick={startCamera}
              style={{
                padding: '6px 12px',
                background: 'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
                color: '#fff',
                border: '1px solid #5a3a1a',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Turn on camera
            </button>
          ) : (
            <button
              onClick={stopCamera}
              disabled={recording}
              style={{
                padding: '6px 12px',
                backgroundColor: recording ? '#3a2510' : 'transparent',
                color: recording ? '#806040' : '#a08060',
                border: '1px solid #5a3a1a',
                borderRadius: 4,
                fontSize: 12,
                cursor: recording ? 'not-allowed' : 'pointer',
              }}
            >
              Turn off
            </button>
          )}
        </div>

        {error && (
          <div style={{ color: '#e74c3c', fontSize: 12 }}>{error}</div>
        )}
        {tabHidden && recording && (
          <div style={{
            color: '#fff',
            backgroundColor: '#e74c3c',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
          }}>
            Tab backgrounded mid-recording — bring this tab back to the foreground!
          </div>
        )}
      </div>
    </div>
  );
});

CameraRecorder.displayName = 'CameraRecorder';

export default CameraRecorder;
